import { memo, useContext } from 'react'
import { RouteSkeletonScope, eventRouteOpensOverview } from './route-skeleton-scope'
import { cva, type VariantProps } from 'class-variance-authority'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  CardSkeleton,
  DashboardBodySkeleton,
  EditorBodySkeleton,
  EventSkeleton,
  ListBodySkeleton,
  CalendarBodySkeleton,
  TabbedListBodySkeleton,
  MessagesSkeleton,
  ParticipantEventSkeleton,
  SettingsBodySkeleton,
  TitleSkeleton,
} from './card-skeleton'
import { PageShell } from './page-shell'
import { placed } from '@/lib/style-guard'

const spinnerVariants = cva(
  'shrink-0 animate-spin rounded-full border-[var(--color-primary-400)] border-t-transparent',
  {
    variants: {
      size: {
        sm: 'size-4 border-2',
        default: 'size-6 border-2',
        lg: 'size-9 border-3',
        xl: 'size-12 border-3',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const Spinner = memo(function Spinner({
  className,
  size,
  onDark = false,
}: { className?: string; onDark?: boolean } & VariantProps<typeof spinnerVariants>) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(spinnerVariants({ size }), onDark && 'border-[var(--hairline-on-dark)] border-t-transparent', placed(className, 'Spinner'))}
      role="status"
      aria-label={t('common.loading')}
    >
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  )
})

/* A placeholder takes the SHAPE of what it stands for (round for an avatar, the card's radius for a
   card) and the hero card's chip fill on a hero card. Props since 30.10.2026, not page classes. */
const SKELETON_SHAPE = { default: 'rounded-[var(--radius-md)]', round: 'rounded-full', lg: 'rounded-[var(--radius-lg)]', xl: 'rounded-[var(--radius-xl)]', '2xl': 'rounded-[var(--radius-2xl)]', button: 'rounded-[var(--btn-radius)]' } as const
const Skeleton = memo(function Skeleton({ className, shape = 'default', onHero = false }: { className?: string; shape?: keyof typeof SKELETON_SHAPE; onHero?: boolean }) {
  return (
    <div
      className={cn(
        // `skeleton-blink`: the shared loading pulse (Parley motion), not Tailwind's own.
        'skeleton-blink',
        SKELETON_SHAPE[shape],
        onHero ? 'bg-[var(--hero-card-chip)]' : 'bg-[var(--surface-muted)]',
        placed(className, 'Skeleton')
      )}
    />
  )
})

/**
 * A page or tab still loading: the shape of a Parley page (figure cards over a list card),
 * blinking, so the content arrives where it will stay. Was a centred spinner, which told the
 * reader nothing about what was coming (Parley motion, 24.10.2026).
 */
function PageLoader({ title = false }: { title?: boolean }) {
  const { t } = useTranslation()
  return (
    <div role="status" aria-label={t('common.loading')} className="space-y-4">
      <span className="sr-only">{t('common.loading')}</span>
      {title && <Skeleton className="h-8 w-56" />}
      <div className="grid gap-4 md:grid-cols-3">
        <CardSkeleton shape="figure" />
        <CardSkeleton shape="figure" className="max-md:hidden" />
        <CardSkeleton shape="figure" className="max-md:hidden" />
      </div>
      <CardSkeleton shape="rows" rows={4} />
    </div>
  )
}

/**
 * The placeholder for a route that is still loading its code or data, chosen by WHERE the reader is
 * going, so every step of the load draws the same shape (David's recording, 24.10.2026: opening an
 * event went page skeleton, blank, event skeleton, a second blank, then the tab's own grey slabs).
 * Each page draws the same component while its own data loads, so shell, route and page agree.
 */
function RouteSkeleton() {
  const { t } = useTranslation()
  const scope = useContext(RouteSkeletonScope)
  if (scope === 'none') return null
  const { pathname, search } = window.location
  const status = (body: React.ReactNode) => (
    <div role="status" aria-label={t('common.loading')}>
      <span className="sr-only">{t('common.loading')}</span>
      {body}
    </div>
  )
  if (scope === 'body') return status(<CardSkeleton shape="rows" rows={4} />)
  if (/^\/event\/[0-9a-f-]{36}\/?$/i.test(pathname)) {
    const participant = new URLSearchParams(search).get('modus') === 'deltaker'
    return <PageShell width="data">{status(participant ? <ParticipantEventSkeleton /> : <EventSkeleton overview={eventRouteOpensOverview(search)} />)}</PageShell>
  }
  // Each in the width its page uses, so the placeholder does not jump sideways when the page lands.
  const page = (body: React.ReactNode, width?: 'data') => <PageShell width={width}>{status(<div className="space-y-6"><TitleSkeleton />{body}</div>)}</PageShell>
  if (pathname === '/' || pathname === '/dashboard') return page(<DashboardBodySkeleton />, 'data')
  if (pathname === '/events' || /^\/(offers|notifications|catalogue|presentations|destinations)\/?$/.test(pathname)) return page(<ListBodySkeleton kpis={pathname === '/events' || pathname === '/offers'} />, 'data')
  if (pathname === '/calendar') return page(<CalendarBodySkeleton />)
  if (pathname.startsWith('/kommunikasjon')) return page(<TabbedListBodySkeleton />)
  if (pathname.startsWith('/messages')) return status(<MessagesSkeleton />)
  if (/^\/event\/[0-9a-f-]{36}\/edit\/?$/i.test(pathname)) return page(<EditorBodySkeleton />)
  if (pathname.startsWith('/settings')) return page(<SettingsBodySkeleton />)
  return <PageLoader title />
}

export { Spinner, Skeleton, PageLoader, RouteSkeleton }
